import { useEffect } from 'react';
import { useSpotMarkets } from 'src/hooks/spotMarkets/useSpotMarkets';
import useS3Params from 'src/hooks/useS3Params';
import useAlertNotify from 'src/components/Toasts/AlertNotifications/hooks/useAlertNotify';
import useModal from 'src/components/Modals/hooks/useModal';

export default function useS3ParamsAlertNotificationListener() {
	const s3Params = useS3Params();
	const alertNotify = useAlertNotify();

	// For collateral alert.
	const spotMarkets = useSpotMarkets();
	const { show } = useModal();

	const s3ParamsAlert = s3Params?.alert;

	const isAlertExpired =
		s3ParamsAlert &&
		s3ParamsAlert.alertData.untilMs &&
		s3ParamsAlert.alertData.untilMs < Date.now();

	useEffect(() => {
		if (!s3ParamsAlert?.alertType || isAlertExpired) {
			return;
		}

		switch (s3ParamsAlert.alertType) {
			case 'feature': {
				if (
					!s3ParamsAlert.alertData.heading ||
					!s3ParamsAlert.alertData.description
				) {
					return;
				}

				const { heading, description, linkText, linkUrl, imageSrc } =
					s3ParamsAlert.alertData;

				alertNotify({
					id: `feature-alert-${heading}`,
					// TODO: We don't have image uploads implemented in the BE so keeping
					// this `minor` for now in order to mitigate any mistakes. Need to update
					// to `major` once BE is complete.
					alertType: 'minor',
					imageSrc,
					message: heading,
					description,
					action: {
						type: 'link',
						label: linkText,
						href: linkUrl ?? '',
					},
				});

				return;
			}
			case 'warning': {
				if (
					!s3ParamsAlert.alertData.heading ||
					!s3ParamsAlert.alertData.description
				) {
					return;
				}

				const { heading, description, linkText, linkUrl } =
					s3ParamsAlert.alertData;

				alertNotify({
					id: `warning-alert-${heading}`,
					alertType: 'minor',
					message: heading,
					description,
					action: {
						type: 'link',
						label: linkText,
						href: linkUrl ?? '',
					},
				});

				return;
			}
			case 'market': {
				if (!s3ParamsAlert.alertData.baseSymbol) {
					return;
				}

				const { baseSymbol } = s3ParamsAlert.alertData;

				alertNotify({
					id: `market-alert-${baseSymbol}`,
					alertType: 'minor',
					message: `${baseSymbol} has landed!`,
					description: `Start trading ${baseSymbol}-PERP today!`,
					action: {
						type: 'link',
						label: 'Trade now',
						href: `/${baseSymbol}-PERP`,
					},
				});

				return;
			}
			case 'collateral': {
				if (!s3ParamsAlert.alertData.baseSymbol) {
					return;
				}

				const { baseSymbol } = s3ParamsAlert.alertData;
				const spotMarket = spotMarkets.find(
					(spotMarket) => spotMarket.symbol === baseSymbol
				);

				alertNotify({
					id: `collateral-alert-${baseSymbol}`,
					alertType: 'minor',
					message: `${baseSymbol} is now live!`,
					description: `You can now deposit ${baseSymbol}.`,
					action: {
						label: `Deposit`,
						callback: () => {
							show({
								type: 'deposit',
								props: {
									collateralIndex: spotMarket?.marketIndex ?? 0,
								},
							});
						},
					},
				});

				return;
			}
			default:
				return;
		}
	}, [s3ParamsAlert, isAlertExpired, show, spotMarkets, alertNotify]);
}
